import { RouterData } from '-/routes/typings.d'
import numeral from 'numeral'
import _ from 'lodash'
import moment from 'moment'
import crypto from 'crypto-js'

/**
 * 延时
 *
 * @param {number} ms 需要延迟的时长，单位毫秒
 * @returns {Promise<void>}
 */
export const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

/**
 * 扩展的 typeof，用来判断当前变量类型
 *
 * @param {*} obj 别检测变量
 * @returns {string} 数据类型
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const whatType = (obj: any): string =>
  // @ts-ignore
  Object.prototype.toString
    .call(obj)
    .match(/\s([a-z|A-Z]+)/)[1]
    .toLowerCase()

/**
 * 比较 2 个路径之间的关系
 *
 * @param {string} str1 路径 A
 * @param {string} str2 路径 B
 * @returns {(1 | 2 | 3)}
 * 1： str2 是 str1 的子集 如： /aaa/bbb(str2) 和 /aaa/bbb/:id(str1)
 * 2： str1 是 str2 的子集 如： /xxx/yyy(str1) 和 /xxx/yyy/zzz(str2)
 * 3： 无任何关系
 */
function getRelation(str1: string, str2: string): 1 | 2 | 3 {
  if (str1 === str2) {
    console.warn('Two path are equal!')
  }

  const arr1 = str1.split('/')
  const arr2 = str2.split('/')

  if (arr2.every((item, index) => item === arr1[index])) {
    return 1
  }
  if (arr1.every((item, index) => item === arr2[index])) {
    return 2
  }

  return 3
}

/**
 * 获取要渲染的页面路径，剔除深度渲染路径
 *
 * @param {string[]} routePaths
 * @returns {string[]}
 */
function getRenderArr(routePaths: string[]): string[] {
  let renderArr = []

  renderArr.push(routePaths[0])
  for (let i = 1; i < routePaths.length; i += 1) {
    // 去重
    renderArr = renderArr.filter(item => getRelation(item, routePaths[i]) !== 1)

    const shouldAdd = renderArr.every(item => getRelation(item, routePaths[i]) === 3)
    if (shouldAdd) {
      renderArr.push(routePaths[i])
    }
  }

  return renderArr
}

type Routes = {
  key: string
  path: string
  exact: boolean
} & RouterData[keyof RouterData]

/**
 * 获取路由配置
 *
 * { path:{name,...param}} => Array<{name,path ...param}>
 * @param {string} path 地址
 * @param {routerData} routerData
 */
export function getRoutes(path: string, routerData: RouterData): Routes[] {
  // 筛选出以 path 起始且不等于 path 的所有路由地址
  let routePaths = Object.keys(routerData).filter(
    routePath => routePath.indexOf(path) === 0 && routePath !== path
  )

  routePaths = routePaths
    // 替换传入地址为空，如：path='user' /user/name => name
    .map(item => item.replace(path, ''))
    // 过滤因同名导致的无效地址，如：/user-management 经过 replace 后变为 -management
    .filter(item => path === '/' || item.indexOf('/') === 0)

  // 获取要渲染的路由去除深度渲染
  const renderPaths = getRenderArr(routePaths)
  // 转换合并参数
  const renderRoutes = renderPaths.map(renderPath => {
    const exact = !routePaths.some(
      routePath => routePath !== renderPath && getRelation(routePath, renderPath) === 1
    )

    return {
      exact,
      ...routerData[`${path}${renderPath}`],
      key: `${path}${renderPath}`,
      path: `${path}${renderPath}`,
    }
  })

  return renderRoutes
}

/**
 * 是否存在相同元素
 *
 * @export
 * @param {(Array<string | number>)} target 目标数组
 * @returns {boolean} 结果
 */
export function hasSameElement(target: (string | number)[]): boolean {
  const hash: { [index: string]: boolean } = {}
  let result = false

  target.forEach(element => {
    if (hash[element]) {
      result = true
      return
    }
    hash[element] = true
  })

  return result
}

/**
 * 价格转换：分 => 元
 * @param value 分
 */
export function conversionPrice(value: number, withUnit = false): string {
  if (Number.isNaN(value)) {
    // throw new Error('转换值不能是NaN')
    return '0'
  }

  const formattedNumber = numeral(value / 100).format('0,0.00')
  return (withUnit ? '¥' : '') + formattedNumber
}

/**
 * 精度处理
 * @param num
 * @param precision
 */
export function accuracy(num: number, precision: number = 12): number {
  return +parseFloat(num.toPrecision(precision))
}

/**
 * excel导出
 * 防止重复点击
 * @param url
 */
export function exportExcel(url: string): void {
  _.debounce(() => {
    window.open(url, '__target')
  }, 200)()
}

/**
 * 解析URL
 * @param url
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function resolveSearch(search: string, item: string) {
  const sValue = search.match(new RegExp(`[?&]${item}=([^&]*)(&?)`, 'i'))
  return sValue ? decodeURIComponent(sValue[1]) : sValue
}

/**
 * 解析URL（全）
 * @param search
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveSearchToJson(search: string): any {
  const param = {}
  search.replace(/([^?&]+)=([^?&]+)/g, (s, v, k) => {
    param[v] = decodeURIComponent(k)
    return `${k}=${v}`
  })
  return param
}

/**
 * 数组是否包含另一个数组
 */
export function isContained(a: number[] = [], b: number[] = []): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length < b.length) {
    return false
  }

  for (let i = 0; i < b.length; i += 1) {
    let flag = false
    for (let j = 0; j < a.length; j += 1) {
      if (a[j] === b[i]) {
        flag = true
        break
      }
    }
    if (flag === false) {
      return flag
    }
  }

  return true
}

/**
 * 根据年龄生成虚假的出生日期
 */
export function generateToBirth(age = 0): string {
  const now = moment(new Date()).format('YYYYMMDD')

  return now.replace(/^(\d{4})/, value => String(Number(value) - age))
}

/**
 * 格式化文件大小：kb、Mb
 */
export function formatFileSize(fileSize: number): string {
  if (fileSize < 1024 * 1024) {
    return `${(fileSize / 1024).toFixed(2)} KB`
  }
  return `${(fileSize / 1024 / 1024).toFixed(2)} MB`
}

/**
 * 格式化时间戳日期
 */
export function formatDatestamp(datestamp: number): string {
  return moment(datestamp).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * 驼峰转下划线
 */
export function humpToLine(string: string): string {
  return string.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * 密码加密
 */
export function pwdMd5(string: string): string {
  return crypto.MD5(`${crypto.MD5(string)}medlinker`).toString()
}
